import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Hammer, Ruler, HeartHandshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Motion";

const values = [
  { icon: HeartHandshake, title: "Family on every job", text: "The name on the truck is the name on the work. We show up, and we answer the phone." },
  { icon: Ruler, title: "Straight talk, clear quotes", text: "You'll know what's being done, what it costs and when we'll be there — before we start." },
  { icon: Hammer, title: "Clean, careful, finished", text: "We protect your home, tidy up daily and don't leave until the last detail is done." },
];

export const Story = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["-4%", "14%"]);

  return (
    <section id="story" ref={ref} className="grain relative bg-[#1A1714] text-[#FAF9F5] py-24 lg:py-36 overflow-hidden" data-testid="story-section">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="relative lg:col-span-5 h-[460px] sm:h-[560px] lg:h-auto">
          <motion.div style={{ y: y1 }} className="absolute left-0 top-0 w-[78%] aspect-[3/4] overflow-hidden rounded-t-[200px] rounded-b-[20px]">
            <img src="/img/craft1.jpg" alt="Craftsman cutting a wood joint" loading="lazy" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div style={{ y: y2 }} className="absolute right-0 bottom-0 w-[52%] aspect-square overflow-hidden rounded-[20px] border-[6px] border-[#1A1714]">
            <img src="/img/craft2.jpg" alt="Marking a measurement on timber" loading="lazy" className="h-full w-full object-cover" />
          </motion.div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
          <Reveal><Eyebrow light>Our story</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
              Your home, treated like <span className="italic text-[#E8A27F]">family's.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg text-[#D8D2C8] leading-relaxed max-w-xl">
              James Family Home Improvement LLC is a family business built on a simple idea: do the work right, be honest about it,
              and leave every home better than we found it. Led by owner Harold James, we proudly serve homeowners across all of
              North and Central Florida.
            </p>
          </Reveal>
          <div className="mt-12 divide-y divide-[#332D27] border-y border-[#332D27]">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={0.1 + i * 0.08}>
                <div className="flex gap-5 py-6" data-testid={`value-item-${i}`}>
                  <v.icon className="h-6 w-6 shrink-0 text-[#D49A46] mt-1" />
                  <div>
                    <h3 className="font-serif-d text-2xl font-semibold">{v.title}</h3>
                    <p className="mt-1 text-sm text-[#BDB5A9] leading-relaxed">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
